import { useAuth0 } from '@auth0/auth0-react';

// imports for app window layout
import MainWindow from '../../app_layout/main_window/MainWindow';
import ChatWindow from '../../app_layout/chat_window/ChatWindow';
import SideBar from '../../app_layout/sidebar/SideBar';
import APP_WINDOW_CONFIG from '../../app_layout/layout_config';

// imports relevant for HTTP calls
import DisplayException from '../../error_messages/DisplayException';
import CircleLoader from 'react-spinners/CircleLoader';
import {SERVER_URL, WEBSOCKET_URL} from '../../config_server_url';

// import user session information
import UserSessionContext from '../../user_session/context_user_session';
import { SetSessionInfo_OnDeleteCurrentProject, SetSessionInfo_OnDeleteCurrentCampaign } from '../../user_session/session_info_utils';

// imports this module specific
import '../../css_styles/button_styles.css'
import '../../css_styles/window_canvas_styles.css'
import classes from './PageOptimizationCycles.module.css';
import OptimizationCycleNavigation from './OptimizationCycleNavigation';
import CyclePlannerEditor from './CyclePlannerEditor';
import CyclePlannerViewer from './CyclePlannerViewer';
import WorkflowViewer from '../../experimental_setup/WorkflowViewer'
import WorkflowEditorCycle from '../../experimental_setup/WorkflowEditorCycle'
import DesignSpaceViewer from '../../design_space/DesignSpaceViewer'
import DesignSpaceEditorCycle from '../../design_space/DesignSpaceEditorCycle'
import ObjectiveViewer from '../../objective/ObjectiveViewer'
import ObjectiveEditorCycle from '../../objective/ObjectiveEditorCycle'
import InfoPopup from '../../popup_windows/InfoPopup';

import { useNavigate } from 'react-router-dom';
import { useContext, useState, useEffect } from 'react';

function PageOptimizationCycles() {

    // ------------------ Display HTTP/Server Exceptions ---------------------------------
    const [Exception, setException] = useState(false);
    const [ExceptionMessage, setExceptionMessage] = useState('');
    // This Component requires to fetches data on first load -> setIsloading(true);
    // This Component does not fetch data on first load -> setIsloading(false);
    const [isLoading, setIsLoading] = useState(false);

    // ------------------ Load User Session Information ---------------------------------
    const usr_session_ctx = useContext(UserSessionContext);
    const { user } = useAuth0();
    const [broadcastProject, setBroadcastProject] = useState([]); //get session notifications via web sockets
    const [broadcastCampaign, setBroadcastCampaign] = useState([]); //get session notifications via web sockets
    const [onOkBroadcastCampaign, setOnOkBroadcastCampaign] = useState(null);

    // ------------------ Add User Session Websockets For Notification Events Across All Tenant (Workspace) Members -----------------------------

    useEffect(() => {
        // Create WebSocket connection`${SERVER_URL}/all_projects/load/`
        const ws = new WebSocket(`${WEBSOCKET_URL}/user_session/ws_project/${usr_session_ctx.uuid_tenant}/${user.sub}`);
        // Connection opened
        ws.onopen = () => {
        //   console.log(`WebSocket connection established for tenant ${usr_session_ctx.uuid_tenant}, user ${user.sub}`);
        };
        ws.onerror = (error) => {
            // console.error("WebSocket error observed:", error);
          };
        // Listen for messages
        ws.onmessage = (event) => {
        //   console.log('Message received:', event.data);
          let parser = event.data.split('__');
          let sender = parser[0]
          let type = parser[1].split('.')[1]
          let project_uuid = parser[2]
          if (type === 'ProjectDelete'){
            if(project_uuid === usr_session_ctx.session_info.uuid_current_project) {
                let new_broadcast = (<>{sender} has deleted <strong>your current project uuid:{project_uuid}</strong></>);
                usr_session_ctx.session_info = SetSessionInfo_OnDeleteCurrentProject(usr_session_ctx);
                handleUpdateUserSession()
                setBroadcastProject(prevBroadcast => [...prevBroadcast, new_broadcast]);
            }
          }
        };
        // Connection closed
        ws.onclose = () => {
        //   console.log(`WebSocket connection closed for tenant ${usr_session_ctx.uuid_tenant}, user ${user.sub}`);
        };
        // Clean up WebSocket connection when component unmounts
        return () => {
          ws.close();
        };
      }, [usr_session_ctx.uuid_tenant]); // Effect runs when tenant_id or user_id changes


      function handleUpdateUserSession() {
        setIsLoading(true);
        let session_update = {key: 'session_info', value: usr_session_ctx.session_info}
        fetch(`${SERVER_URL}/user_session/update_field/`, {method: 'PUT',
                                                           headers:{'Content-Type':'application/json',
                                                                   'auth0_user': user.sub,
                                                                   'uuid_tenant': usr_session_ctx.uuid_tenant }, 
                                                           body:JSON.stringify(session_update)
        })
        .then((res) => {
        if(!res.ok) {
            throw new Error(`HTTP error ${res.status}`);
        }
            return res.json();
        })
        .then((data) => {
            setIsLoading(false);
        })
            .catch((err) => {
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        });
   }

   useEffect(() => {
    const ws = new WebSocket(`${WEBSOCKET_URL}/user_session/ws_campaign/${usr_session_ctx.uuid_tenant}/${user.sub}`);
    ws.onopen = () => {
    //   console.log(`WebSocket connection established for tenant ${usr_session_ctx.uuid_tenant}, user ${user.sub}`);
    };
    ws.onerror = (error) => {
        // console.error("WebSocket error observed:", error);
    };
    // Listen for messages
    ws.onmessage = (event) => {
        // console.log('Message received:', event.data);
        let parser = event.data.split('__');
        let sender = parser[0]
        let type = parser[1].split('.')[1]
        let project_uuid = parser[2]
        let campaign_uuid = parser[3]
        if (project_uuid === usr_session_ctx.session_info.uuid_current_project && campaign_uuid === usr_session_ctx.session_info.uuid_current_opt_campaign){
            if (type === 'CampaignDelete'){
                    let new_broadcast = (<>{sender} has deleted <strong>your current campaign uuid:{campaign_uuid}</strong></>);
                    usr_session_ctx.session_info = SetSessionInfo_OnDeleteCurrentCampaign(usr_session_ctx);
                    handleUpdateUserSession()
                    setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                    setOnOkBroadcastCampaign(() => () => {
                        setBroadcastCampaign([]); // Clear broadcast state
                        setCurrentCampaignExists(false);  // 
                      });
            }
            if (type === 'UpdateCyclePlanner'){
                let new_broadcast = (<>{sender} has updated the cycle planner</>);
                handleUpdateUserSession()
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                    setBroadcastCampaign([]); // Clear broadcast state
                    loadCampaignData();  // 
                  });
            }
            if (type === 'UpdateCycleCandidates'){
                let new_broadcast = (<>{sender} has updated cycle candidates</>);
                handleUpdateUserSession()
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                    setBroadcastCampaign([]); // Clear broadcast state
                    loadCampaignData();  // 
                  });
            }
            if (type === 'CampaignCycleCreate'){
                let new_broadcast = (<>{sender} has created a new cycle.</>);
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                        setBroadcastCampaign([]); // Clear broadcast state
                        loadCampaignData()  // 
                    });
            }
            if (type === 'CampaignCycleDelete'){
                let new_broadcast = (<>{sender} has deleted a cycle for campaign uuid:{campaign_uuid}</>);
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                        setBroadcastCampaign([]); // Clear broadcast state
                        loadCampaignData()  // 
                    });
            }
            if (type === 'CampaignDataUpload'){
                let new_broadcast = (<>{sender} has uploaded new data.</>);
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                    setBroadcastCampaign([]); // Clear broadcast state
                    loadCampaignData()  // 
                });
            }
            if (type === 'CampaignDataRemoved'){
                let new_broadcast = (<>{sender} has removed data.</>);
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                    setBroadcastCampaign([]); // Clear broadcast state
                    loadCampaignData()  // 
                });
            }
            if (type === 'UpdateCampaignExperimentalWorkflow'){
                let new_broadcast = (<>{sender} has updated active Experimental Workflow.</>);
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                    setBroadcastCampaign([]); // Clear broadcast state
                    loadCampaignData()
                  });
            }
            if (type === 'UpdateCampaignDesignSpace'){
                let new_broadcast = (<>{sender} has updated active Design Space.</>);
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                    setBroadcastCampaign([]); // Clear broadcast state
                    loadCampaignData()
                  });
            }
            if (type === 'UpdateCampaignObjective'){
                let new_broadcast = (<>{sender} has updated active Objective.</>);
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                    setBroadcastCampaign([]); // Clear broadcast state
                    loadCampaignData()
                  });
            }
            if (type === 'UpdateAsNewCampaignExperimentalWorkflow'){
                let new_broadcast = (<>{sender} has updated active Experimental Workflow as new object.</>);
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                    setBroadcastCampaign([]); // Clear broadcast state
                    loadCampaignData()
                  });
            }
            if (type === 'UpdateAsNewCampaignDesignSpace'){
                let new_broadcast = (<>{sender} has updated active Design Space as new object.</>);
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                    setBroadcastCampaign([]); // Clear broadcast state
                    loadCampaignData()
                  });
            }
            if (type === 'UpdateAsNewCampaignObjective'){
                let new_broadcast = (<>{sender} has updated active Objective as new object.</>);
                setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                setOnOkBroadcastCampaign(() => () => {
                    setBroadcastCampaign([]); // Clear broadcast state
                    loadCampaignData()
                  });
            }
        }
    };
    ws.onclose = () => {
    //   console.log(`WebSocket connection closed for tenant ${usr_session_ctx.uuid_tenant}, user ${user.sub}`);
    };
    // Clean up WebSocket connection when component unmounts
    return () => {
    ws.close();
    };
}, []); // Effect runs when tenant_id or user_id changes

// ------------------ General Window Layout -----------------------------
    
    const [canvasSize, setCanvasSize] = useState({width: 0, height: 0});
    const [layout, setLayout] = useState({sidebar:0, main: 0, chat: 0});
    const [toggleSideBar, setToggleSideBar] = useState(false);

    useEffect(() => {
        function handleResize() {
            let window_height = window.innerHeight;
            let window_width = window.innerWidth;
            let canvas_height = window_height - APP_WINDOW_CONFIG.OFFSET_HEADER_YPOS_PX;
            let sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_PX;
            let chat = (window_width - sidebar)*APP_WINDOW_CONFIG.CHAT_WIDTH_RELATIVE;
            chat = Math.max (chat, APP_WINDOW_CONFIG.MIN_CHAT_WIDTH_PX);
            let main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            setCanvasSize({width: window_width, height: canvas_height});
            // if currently the sidebar is not toggled, then resize the sidbar widow to toggle_size
            if(toggleSideBar){
                sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_COLLAPSED_PX;
                main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            }
            setLayout({sidebar:sidebar, main: main, chat: chat});
        }
        // Set initial height
        handleResize();
        // Add event listener to handle window resize
        window.addEventListener('resize', handleResize);
        // Remove event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [toggleSideBar]); // Empty dependency array ensures the effect only runs once


    function handleToggle() {
        // if currently the sidebar is not toggled, then resize the sidbar widow to toggle_size
        if(!toggleSideBar){
            let window_width = window.innerWidth;
            let sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_COLLAPSED_PX;
            let chat = layout.chat;
            let main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            setLayout({sidebar:sidebar, main: main, chat: chat});
        }
        else
        {
            let window_width = window.innerWidth;
            let sidebar = APP_WINDOW_CONFIG.SIDEBAR_WIDTH_PX;
            let chat = layout.chat;
            let main = window_width - sidebar - chat - APP_WINDOW_CONFIG.OFFSET_WINDOW_RIGHT_PX;
            setLayout({sidebar:sidebar, main: main, chat: chat});
        }
        setToggleSideBar(!toggleSideBar);
    }

    // ------------------ Side Bar Content -----------------------------

    const sidebar = (<>
        <SideBar width={layout.sidebar} 
                toggle={toggleSideBar} 
                handle_toggle={handleToggle} 
                active_link='optimization_cycles' >
        </SideBar>
    </>)

    // ------------------ Main Window Content ----------------------------
    
    const navigate = useNavigate();
    const [cyclesStatus, setCyclesStatus] = useState(null);
    const [viewerMode, setViewerMode] = useState('Planner'); 
    const [currentCampaignExists, setCurrentCampaignExists] = useState(false)
    const [existingCycles, setExistingCycles] = useState(false);
    const [cycleId, setCycleId] = useState(-1);
    const [triggerReload, setTriggerReload] = useState(0);
    const [cycleSetupViewer, setCycleSetupViewer] = useState('')
    const [cycleSetupEditor, setCycleSetupEditor] = useState('')
    const [cycleSetup, setCycleSetup] = useState({})
    const [cycleSetupPrev, setCycleSetupPrev] = useState({})
    const [uuidOptCampaign, setUuidOptCampaign] = useState('')
    const [campaignSetupOnUpdate, setCampaignSetupOnUpdate] = useState(false)
    const [setupUpdatedBy, setSetupUpdatedBy] = useState('')
    const [popupUpdatedBy, setPopupUpdatedBy] = useState(false);


    async function loadCampaignData(){
        if (usr_session_ctx.session_info.uuid_current_opt_campaign !== '') { 
            setCurrentCampaignExists(true); 
            setIsLoading(true); // Set loading state to true when fetch begins

            let uuid_opt_campaign = usr_session_ctx.session_info.uuid_current_opt_campaign;
            setUuidOptCampaign(uuid_opt_campaign);

            try {
                // Fetch the cycles activity status
                const res = await fetch(`${SERVER_URL}/current_opt_campaign/load_all_cycles_activity_status/`, { 
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'uuid_tenant': usr_session_ctx.uuid_tenant
                    },
                    body: JSON.stringify(uuid_opt_campaign)
                });

                if (!res.ok) {
                    setIsLoading(false);
                    throw new Error(`HTTP error ${res.status}`);
                }

                const cycles_status = await res.json();
                if(cycles_status.msg === ''){
                    setCyclesStatus(cycles_status.data);
                    if (cycles_status.data.length === 0) {
                        setExistingCycles(false);
                        setCycleId(-1);
                    } else {
                        setCycleId(cycles_status.data.length - 1);
                        setExistingCycles(true);
                    }
                    const is_updated_by_other = await isCampaignOnModifyByOtherUser(uuid_opt_campaign);
                    if (is_updated_by_other.is_under_update === true) {
                        setSetupUpdatedBy(is_updated_by_other.under_update_by);
                    }
                    setIsLoading(false);
                }
                else{
                    let new_broadcast = (<>Your current campaign does not exist anymore.</>);
                    usr_session_ctx.session_info = SetSessionInfo_OnDeleteCurrentCampaign(usr_session_ctx);
                    handleUpdateUserSession()
                    setBroadcastCampaign(prevBroadcast => [...prevBroadcast, new_broadcast]);
                    setOnOkBroadcastCampaign(() => () => {
                        setBroadcastCampaign([]); // Clear broadcast state
                        loadCampaignData();    
                      });
                    setIsLoading(false); 

                }
            } catch (err) {
                setException(true);
                setIsLoading(false);
                setExceptionMessage(`HTTPException: err_msg=${err.message}`);
            }
        } else { 
            setCurrentCampaignExists(false);
        }
    }

    useEffect(() => {
        loadCampaignData();
    }, [usr_session_ctx.session_info.uuid_current_opt_campaign, triggerReload]);


    function isCampaignOnModifyByOtherUser(uuid_opt_campaign) {
        // Return the fetch promise so it can be awaited
        return fetch(`${SERVER_URL}/current_opt_campaign/is_under_update_by_other_user/`, { 
            method: 'POST',
            headers: {
                'Content-Type':'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            },
            body: JSON.stringify(uuid_opt_campaign)
        })
        .then((res) => {
            if (!res.ok) {
                throw new Error(`HTTP error ${res.status}`);
            }
            return res.json();
        })
        .then((data) => {
            return data.data; // Return the actual data here
        })
        .catch((err) => {
            setException(true);
            setIsLoading(false);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
            return null; // Return a default value in case of error
        });
    }



    // Create Cycle
    async function createNewCycle(){
        if(usr_session_ctx.session_info.on_update_campaign.uuid_experimental_setup!==''){
            setCampaignSetupOnUpdate(true);
        }
        else if(setupUpdatedBy !== ''){
            setPopupUpdatedBy(true);
        }
        else{
            setIsLoading(true);
            try{
                let current_cycle_id = null
                if (cycleId >= 0){
                    current_cycle_id = cycleId;
                }
                let data = {uuid_project: usr_session_ctx.session_info.uuid_current_project,
                            uuid_opt_campaign: usr_session_ctx.session_info.uuid_current_opt_campaign,
                            current_cycle_id: current_cycle_id}
                let headers = {
                    'Content-Type': 'application/json',
                    'auth0_user': user.sub,
                    'uuid_tenant': usr_session_ctx.uuid_tenant
                };
                const response = await fetch(`${SERVER_URL}/current_opt_campaign/create_new_opt_cycle/`, {
                                method: 'POST',
                                headers: headers,
                                body: JSON.stringify(data)
                });
                if(response.ok){
                    const data = await response.json();
                    if (data.data ==='status ok'){
                        setTriggerReload(triggerReload+1);
                    }
                }
                if(!response.ok){
                    throw new Error(`HTTP error ${response.status}`);
                }
                setIsLoading(false);
            } catch(err) {
                setIsLoading(false);
                setException(true);
                setExceptionMessage(`HTTPException: err_msg=${err.message}`);
            }
        }
    }


     // Delete Active Cycle
     async function deleteActiveCycle(){
        setIsLoading(true);
        try{
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const data = {uuid_project: usr_session_ctx.session_info.uuid_current_project,
                          uuid_opt_campaign: usr_session_ctx.session_info.uuid_current_opt_campaign
                         }
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/delete_last_opt_cycle/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(data)
            });
            if(response.ok){
                const data = await response.json();
                if (data.data ==='status ok'){
                    setCycleId(cycleId-1);
                    setTriggerReload(triggerReload+1);
                }
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
            setIsLoading(false);
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
    }

    //  ----------- Load information about cycle setup
    async function loadCycleData(cycle_id){
        setIsLoading(true); // Set loading state to true when fetch begins
        const data = {
            uuid_opt_campaign: usr_session_ctx.session_info.uuid_current_opt_campaign,
            cycle_id: cycle_id,
        };
        try {
            // Prepare the fetch requests
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/load_optimization_cycle/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'uuid_tenant': usr_session_ctx.uuid_tenant
                },
                body: JSON.stringify(data),
            });
            // Check responses
            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
            }
            // Parse JSON from responses
            const loaded_cycle = await response.json();
            // Update state with loaded data
            // Set loading state to false
            setIsLoading(false);
            return loaded_cycle.data
        } catch (err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
    };


    async function handleViewSetupObjects(event){
        let parser = event.target.id.split('_');
        let campaign_object = parser[1];
        let cycle_id = parseInt(parser[2]);
        setCycleSetupViewer(campaign_object)
        // console.log(campaign_object)
        // console.log(cycle_id)
        const cycle_data = await loadCycleData(cycle_id)
        setCycleSetup(cycle_data.campaign_setup)
    }


    async function handleEditSetupObjects(event){
        let parser = event.target.id.split('_');
        let campaign_object = parser[1];
        let cycle_id = parseInt(parser[2]);
        setCycleSetupEditor(campaign_object)
        // console.log(campaign_object)
        const cycle_data = await loadCycleData(cycle_id)
        setCycleSetup(cycle_data.campaign_setup)
        if(cycle_id>0)
            {
              const cycle_data2 = await loadCycleData(cycle_id-1)
              setCycleSetupPrev(cycle_data2.campaign_setup)
        }
    }

    function onExit(){
        setCycleSetupViewer('');
        setCycleSetupEditor('');
        setCycleSetupPrev({})
    }



    // ----------- Define Navigation Bar to switch between viewer modes: Experimental Setup, Design Space, Objective

    function selectViewerMode(event){
        event.preventDefault();
        setViewerMode(event.target.id);
    }

    let classPL = 'black';
    let classAC = 'black';
    let classTD = 'black';
    let classSI = 'black';

    let page_content = (<> </>);   

    if (!existingCycles){
        page_content = (<>
                <div style={{fontSize:'18px', position:'relative', textAlign:'center', height:'fit-content', marginTop:'50px', width:'100%'}} >
                There aren't any cycles yet.
                </div> 
                </>);
    }

    if (viewerMode === 'Planner'){
        classPL = 'selectionLink selected';
        classAC = 'selectionLink';
        classTD = 'selectionLink';
        classSI = 'selectionLink';
        if (existingCycles){
            let uuid_opt_campaign = usr_session_ctx.session_info.uuid_current_opt_campaign
            let cycleActive = cyclesStatus[cycleId];

            if (cycleSetupViewer === 'experimentalWorkflow')
                page_content=(<>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                    <div className = 'inner_canvas_shadow' style={{height:canvasSize.height-70, top:'30px'}}>
                        <WorkflowViewer uuid_experimental_setup={cycleSetup.uuid_experimental_setup} 
                                    width={layout.main} 
                                    height={canvasSize.height-60} 
                                    hideActionPanel={true}/>
                        <button className='actionButton'  
                                style={{marginLeft:'30px', marginTop:'15px', marginBottom:'20px'}} 
                                onClick={onExit}>Exit
                        </button>
                    </div>
                </div>
                </>);

            else if (cycleSetupEditor === 'experimentalWorkflow'){
                page_content=(<>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                        <WorkflowEditorCycle 
                                uuid_opt_campaign ={uuidOptCampaign}
                                campaign_objects={cycleSetup}
                                campaign_objects_prevcycle={cycleSetupPrev}
                                width={layout.main}
                                height={canvasSize.height-60}
                                exit={onExit}
                                cycleId={cycleId}
                                />
                </div>
                </>);
            }

            else if (cycleSetupViewer === 'designSpace'){
                page_content=(<>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                    <div className = 'inner_canvas_shadow' style={{height:canvasSize.height-70, top:'30px'}}>
                        <DesignSpaceViewer campaign_objects={cycleSetup}
                               width={layout.main}
                               height={canvasSize.height-60}
                               validated={true}
                               hideActionPanel={true}/>
                        <button className='actionButton'  
                                style={{marginLeft:'30px', marginTop:'15px', marginBottom:'20px'}} 
                                onClick={onExit}>Exit
                        </button>
                    </div>
                </div>
                </>);
            }

            else if (cycleSetupEditor === 'designSpace'){
                page_content=(<>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                        <DesignSpaceEditorCycle 
                                uuid_opt_campaign ={uuidOptCampaign}
                                campaign_objects={cycleSetup}
                                campaign_objects_prevcycle={cycleSetupPrev}
                                width={layout.main}
                                height={canvasSize.height-60}
                                exit={onExit}
                                cycleId={cycleId}
                                />
                </div>
                </>);
            }


            else if (cycleSetupViewer === 'objective'){
                page_content=(<>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                    <div className = 'inner_canvas_shadow' style={{height:canvasSize.height-70, top:'30px'}}>
                        <ObjectiveViewer   campaign_objects={cycleSetup} 
                               width={layout.main}
                               height={canvasSize.height-60}
                               validated={true}
                               hideActionPanel={true}/>
                        <button className='actionButton'  
                                style={{marginLeft:'30px', marginTop:'15px', marginBottom:'20px'}} 
                                onClick={onExit}>Exit
                        </button>
                    </div>
                </div>
                </>);
            }

            else if (cycleSetupEditor === 'objective'){
                page_content=(<>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
                        <ObjectiveEditorCycle 
                                uuid_opt_campaign ={uuidOptCampaign}
                                campaign_objects={cycleSetup}
                                campaign_objects_prevcycle={cycleSetupPrev}
                                width={layout.main}
                                height={canvasSize.height-60}
                                exit={onExit}
                                cycleId={cycleId}
                                />
                </div>
                </>);
            }


            else if (cycleActive){
                page_content=(<>
                        <CyclePlannerEditor width={layout.main} 
                                            height={canvasSize.height-150}
                                            cycle_id={cycleId}
                                            uuid_opt_campaign={uuid_opt_campaign}
                                            handleCreateCycle={createNewCycle}
                                            onView={handleViewSetupObjects}
                                            onEdit={handleEditSetupObjects}
                                            user={user.sub}
                                            />
                </>);
            }
            else if (!cycleActive){
                page_content=(<>

                        <CyclePlannerViewer width={layout.main} 
                                            height={canvasSize.height-150}
                                            cycle_id={cycleId}
                                            uuid_opt_campaign={uuid_opt_campaign}
                                            onView={handleViewSetupObjects}/>
                </>);
            }
        }
    }
    // if (viewerMode === 'Analyze_Candidates'){
    //     classPL = 'selectionLink';
    //     classAC = 'selectionLink selected';
    //     classTD = 'selectionLink';
    //     classSI = 'selectionLink';
    //     if (existingCycles){
    //         page_content=(<>
    //          </>);
    //     }
    // }

    // if (viewerMode === 'Training_Data'){
    //     classPL = 'selectionLink ';
    //     classAC = 'selectionLink';
    //     classTD = 'selectionLink selected';
    //     classSI = 'selectionLink';
    //     if (existingCycles){
    //         page_content=(<>
    //          </>);
    //     }
    // }

    // if (viewerMode === 'Scientific_Informatics'){
    //     classPL = 'selectionLink ';
    //     classAC = 'selectionLink';
    //     classTD = 'selectionLink';
    //     classSI = 'selectionLink selected';
    //     if (existingCycles){
    //         page_content=(<>
    //          </>);
    //     }
    // }

    function handleLoad(){
        navigate('/optimization_campaigns');
    }

    function handleOnCreate(){
        navigate('/campaign_builder');
    }

    function nextCycle(){
        setCycleId(cycleId+1);
    }
   
    function prevCylce(){
        setCycleId(cycleId-1);
    }



    // -------------------- Create Main Window -----------------------------------------------
    let mainWindow = (<MainWindow width={layout.main} />);

    // if no workflow is selected or created
    if (!isLoading && !Exception && !currentCampaignExists && broadcastProject.length === 0 && broadcastCampaign.length === 0) { 
        mainWindow = (<>
            <MainWindow width={layout.main} >
                <div style={{fontSize:'18px', position:'relative', textAlign:'center', height:'fit-content', marginTop:'50px', width:'100%'}} >
                There is no optimization campaign in focus. <br />Create or load an optimization campaign.
                </div> 
                <div style={{position: 'relative', display: 'flex', gap:'20px', justifyContent:'center', marginTop:'25px', left:'3%', width:'94%'}}>
                    <button className={'actionButton'} style={{width:'150px'}} onClick={handleOnCreate} >Create Campaign</button>
                    <button className={'actionButton'} style={{width:'150px', marginRight:'0px'}} onClick={handleLoad}>Load Campaign</button>
                </div>                
            </MainWindow>
        </>);
    }

    if (!isLoading && !Exception && currentCampaignExists && broadcastProject.length === 0 && broadcastCampaign.length === 0) { 
        const numCycles = cyclesStatus.length;
        let cycleActive = false;
        if(numCycles>0) {
            cycleActive = cyclesStatus[cycleId];
        }
        mainWindow = (<>
            <MainWindow width={layout.main} >
                <OptimizationCycleNavigation 
                    cycleId={cycleId}
                    cycleActive={cycleActive}
                    numCycles={numCycles}
                    handleCyclePrev={prevCylce}
                    handleCycleNext={nextCycle}
                    handleCreateCycle={createNewCycle}
                    handleDeleteCycle={deleteActiveCycle} />
                <div style={{position:'relative', left:'5%', top:'12px', display:'flex', gap:'10px', marginTop:'5px', zIndex:'100', hight:'fit-content', overflowY:'hidden', width:'91%'}}>        
                    <button id='Planner' className={classPL} onClick={selectViewerMode} >Planner</button>
                    {/* <button id='Analyze_Candidates' className={classAC} onClick={selectViewerMode}>Analyze Candidates</button> */}
                    {/* <button id='Training_Data'className={classTD} onClick={selectViewerMode}>Training Data</button> */}
                    {/* <button id='Scientific_Informatics'className={classSI} onClick={selectViewerMode}>Scientific Informatics</button> */}
                </div>    
                <div className = {classes.inner_canvas} style={{height:`${canvasSize.height-140}px`, paddingTop:'20px', paddingBottom:'20px'}}>
                        {page_content}
                </div>
            </MainWindow>
        </>);
        if(campaignSetupOnUpdate){
            mainWindow = (<>
                <MainWindow width={layout.main} >
                <OptimizationCycleNavigation 
                    cycleId={cycleId}
                    cycleActive={cycleActive}
                    numCycles={numCycles}
                    handleCyclePrev={prevCylce}
                    handleCycleNext={nextCycle}
                    handleCreateCycle={createNewCycle}
                    handleDeleteCycle={deleteActiveCycle} />
                <div style={{position:'relative', left:'5%', top:'12px', display:'flex', gap:'10px', marginTop:'5px', zIndex:'100', hight:'fit-content', overflowY:'hidden', width:'91%'}}>        
                    <button id='Planner' className={classPL} onClick={selectViewerMode} >Planner</button>
                    <button id='Analyze_Candidates' className={classAC} onClick={selectViewerMode}>Analyze Candidates</button>
                    <button id='Training_Data'className={classTD} onClick={selectViewerMode}>Training Data</button>
                    <button id='Scientific_Informatics'className={classSI} onClick={selectViewerMode}>Scientific Informatics</button>
                </div>    
                <div className = {classes.inner_canvas} style={{height:`${canvasSize.height-140}px`, paddingTop:'20px', paddingBottom:'20px'}}>
                    <InfoPopup ypos={'60px'} handleOk={()=>{setCampaignSetupOnUpdate(false)} } >
                        Campaign Setup is currently being modified. 
                        Please complete your changes before starting an optimization cycle.
                    </InfoPopup>
                </div>
                </MainWindow>
            </>);
        }
        else if(popupUpdatedBy){
            mainWindow = (<>
                <MainWindow width={layout.main} >
                <OptimizationCycleNavigation 
                    cycleId={cycleId}
                    cycleActive={cycleActive}
                    numCycles={numCycles}
                    handleCyclePrev={prevCylce}
                    handleCycleNext={nextCycle}
                    handleCreateCycle={createNewCycle}
                    handleDeleteCycle={deleteActiveCycle} />
                <div style={{position:'relative', left:'5%', top:'12px', display:'flex', gap:'10px', marginTop:'5px', zIndex:'100', hight:'fit-content', overflowY:'hidden', width:'91%'}}>        
                    <button id='Planner' className={classPL} onClick={selectViewerMode} >Planner</button>
                    <button id='Analyze_Candidates' className={classAC} onClick={selectViewerMode}>Analyze Candidates</button>
                    <button id='Training_Data'className={classTD} onClick={selectViewerMode}>Training Data</button>
                    <button id='Scientific_Informatics'className={classSI} onClick={selectViewerMode}>Scientific Informatics</button>
                </div>    
                <div className = {classes.inner_canvas} style={{height:`${canvasSize.height-140}px`, paddingTop:'20px', paddingBottom:'20px'}}>
                    <InfoPopup ypos={'60px'} handleOk={()=>{setPopupUpdatedBy(false)} } >
                        <div > The active campaign setup is currently beeing updated by user {setupUpdatedBy}. </div>
                    </InfoPopup>
                </div>
                </MainWindow>
            </>);
        }
        else if (cycleSetupViewer !== '' || cycleSetupEditor !== ''){
            mainWindow = (<>
            <MainWindow width={layout.main} >
                 {page_content}
            </MainWindow>
            </>);
        }

    }

    if (!isLoading && !Exception && broadcastCampaign.length > 0){
        mainWindow = (<>
            <MainWindow width={layout.main} >
            <div style={{position: 'relative', left:'3%', width:'94%', height:'100%'}}>
                <InfoPopup ypos={'60px'} handleOk={onOkBroadcastCampaign} >
                    <div style={{fontWeight:'bold', textAlign:'left'}}>Session has been updated by another user:</div>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', textAlign: 'left' }}>
                        {broadcastCampaign.map((item, index) => (
                        <li key={index}>{item}</li>
                        ))}
                    </ul>
                </InfoPopup>
                </div>
            </MainWindow>
            </>);
    }


    if (!isLoading && !Exception && broadcastProject.length > 0){
        mainWindow = (<>
            <MainWindow width={layout.main} >
            <div style={{position: 'relative', left:'3%', width:'94%', height:'100%'}}>
                <InfoPopup ypos={'60px'} handleOk={()=>{setBroadcastProject(prevBroadcast => []); navigate('/')} } >
                    <div style={{fontWeight:'bold', textAlign:'left'}}>Session has been updated by another user:</div>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', textAlign: 'left' }}>
                        {broadcastProject.map((item, index) => (
                        <li key={index}>{item}</li>
                        ))}
                    </ul>
                </InfoPopup>
                </div>
            </MainWindow>
            </>);
    }
   

    if (isLoading && !Exception) {   
        mainWindow = (<>
                <MainWindow width={layout.main} >
                    <div style = {{position:'absolute', top:'5px', display: 'flex', justifyContent: 'center', alignItems: 'center',  width:'100%', height:'100%', border:'1px solid black'}}>
                        <CircleLoader loading={isLoading} size={40} color='var(--aixelo-green)' />
                    </div>
                </MainWindow>
            </>);
       }


    // Exection and Error Messeges Get Priority and Are Evaluated Before Anything els
    if(Exception){
        mainWindow = (<>
                <MainWindow width={layout.main} >
                    <DisplayException>
                        {ExceptionMessage}
                    </DisplayException>  
                </MainWindow>
            </>);
       }


// ------------------ Return All Components ----------------------------

    // Show Sidebar, MainWindow and ChatWindow
    return(
        <>
        <div style={{position:'relative', display:'grid', gridTemplateColumns: 'auto auto auto', overflow: 'hidden', left:`${APP_WINDOW_CONFIG.OFFSET_WINDOW_LEFT_PX}px`, top:`${APP_WINDOW_CONFIG.OFFSET_HEADER_YPOS_PX}px`, height:canvasSize.height, width:'100%'}}>
            {sidebar}
            {mainWindow}
            <ChatWindow width={layout.chat}/>
        </div>    
        </>
    );

}


export default PageOptimizationCycles;